import { z } from 'zod';

export const ACCOUNT_DOMAINS = ['money', 'project', 'service', 'delivery', 'relationship'] as const;
export type AccountDomain = (typeof ACCOUNT_DOMAINS)[number];

export const accountAssignmentInputSchema = z.object({
  cwCompanyId: z.number().int().positive(),
  companyName: z.string().min(1),
});
export type AccountAssignmentInput = z.infer<typeof accountAssignmentInputSchema>;

export const accountGrantInputSchema = z.object({
  permission: z.string().min(1),
  cwCompanyId: z.number().int().positive().nullable().optional(),
});
export type AccountGrantInput = z.infer<typeof accountGrantInputSchema>;

export const accountActivityCreateSchema = z.object({
  domain: z.enum(ACCOUNT_DOMAINS),
  body: z.string().min(1).max(4000),
  assignedToUserId: z.string().optional(),
  refType: z.string().max(40).optional(),
  refId: z.string().max(64).optional(),
});
export type AccountActivityCreate = z.infer<typeof accountActivityCreateSchema>;

export const accountActivityResolveSchema = z.object({ status: z.enum(['open', 'handled']) });

export interface AttentionThresholds {
  arOverdueDays: number;
  ticketStaleDays: number;
  projectBudgetPct: number;
  changeOrderWaitingDays: number;
  rmaOpenDays: number;
  dispatchSoonHours: number;
}
export const DEFAULT_THRESHOLDS: AttentionThresholds = {
  arOverdueDays: 30,
  ticketStaleDays: 3,
  projectBudgetPct: 0.9,
  changeOrderWaitingDays: 5,
  rmaOpenDays: 14,
  dispatchSoonHours: 48,
};

export interface AttentionItem {
  domain: AccountDomain;
  severity: 'warn' | 'critical';
  title: string;
  detail: string;
  refType?: string;
  refId?: string;
}

export interface MoneyPanel {
  openArTotal: number;
  overdueArTotal: number;
  overdueInvoiceCount: number;
  changeOrdersAwaiting: number;
  /** Recurring monthly revenue (monthly-equivalent). Only the account summary
   * builder computes it; lightweight MoneyPanels (portfolio, backfill) omit it,
   * so it is optional — read sites default to 0. */
  rmrMonthlyTotal?: number;
}

export type HealthTone = 'fresh' | 'normal' | 'aging' | 'stalled';

/** Health spine: 0–3 fresh, 4–7 normal, 8–14 aging, 15+ stalled. */
export function agingTone(days: number): HealthTone {
  if (days <= 3) return 'fresh';
  if (days <= 7) return 'normal';
  if (days <= 14) return 'aging';
  return 'stalled';
}

export interface ProjectPanelItem {
  cwProjectId: number;
  name: string;
  status: string;
  budgetHours: number;
  actualHours: number;
  pctUsed: number;
  atRisk: boolean;
  endDate: string | null;
  // enriched
  percentComplete: number;      // 0–100
  daysRemaining: number | null; // null when no end date
  projectManager: string | null;
  hoursRemaining: number;
  hoursBillable: number;
  hoursInvoiced: number;
  startDate: string | null;
}

export interface ServicePanelItem {
  ticketId: number;
  summary: string;
  status: string;
  ageDays: number;
  lastActivityDays: number;
  stalled: boolean;
  // enriched
  priority: string;
  board: string;
  assignedTo: string | null;
  hoursActual: number;
}

export interface CustomerProfile {
  cwCompanyId: number;
  name: string;
  status: string | null;         // CW company status name
  industry: string | null;       // market or territory name
  customerSince: string | null;  // ISO date from CW _info.dateEntered
  primaryContact: { name: string; phone: string | null; email: string | null } | null;
  phone: string | null;          // company phone
  addressCity: string | null;
  addressState: string | null;
  siteCount: number;
}

export interface TrendPoint {
  weekStart: string; // ISO date (Monday of the week)
  opened: number;
  resolved: number;
  backlog: number;   // cumulative open at end of week
}

export interface ServiceSnapshot {
  openCount: number;
  stalledCount: number;
  unassignedCount: number;
  avgAgeDays: number;
  totalHours: number;
  agingBuckets: { label: '0–3d' | '4–7d' | '8–14d' | '15d+'; tone: HealthTone; count: number }[];
  priorityMix: { label: string; count: number }[];
  boardBreakdown: { label: string; count: number }[];
}

export interface ServiceAnalytics {
  items: ServicePanelItem[];
  snapshot: ServiceSnapshot;
  trend: TrendPoint[];
}

export interface ProjectBurnPoint {
  weekStart: string; // ISO date
  actualCumulative: number;
  budget: number;    // flat portfolio budget line
}

export interface ProjectSnapshot {
  activeCount: number;
  atRiskCount: number;
  avgPercentComplete: number;   // 0–100
  budgetUtilization: number;    // 0–1 (Σactual ÷ Σbudget)
  statusMix: { label: string; count: number }[];
  scheduleHealth: { label: 'On track' | 'At risk' | 'Overdue'; tone: HealthTone; count: number }[];
  hoursBreakdown: { budget: number; actual: number; remaining: number };
}

export interface ProjectFinancial {
  estimatedRevenue: number;
  estimatedCost: number;
  margin: number;       // revenue − cost
  marginPct: number;    // margin ÷ revenue (0 when revenue 0)
  billingRealizationPct: number; // invoiced ÷ actual hours (0 when actual 0)
}

export interface ProjectChangeOrders {
  openCount: number;
  totalValue: number;
  waitingCount: number; // waiting longer than changeOrderWaitingDays
}

export interface ProjectAnalytics {
  items: ProjectPanelItem[];
  snapshot: ProjectSnapshot;
  trend: ProjectBurnPoint[];
  financial: ProjectFinancial;
  changeOrders: ProjectChangeOrders;
}

export interface DeliveryPanelItem {
  scheduleId: number;
  ticketId: number | null;
  name: string;
  tech: string | null;
  start: string;
}
export interface RmaPanelItem { id: number; summary: string; status: string; ticketId: number | null; ageDays: number }
export interface RelationshipPanel {
  salespersonName: string | null;
  programManagerNames: string[];
  openActivityCount: number;
}

export interface AccountSummary {
  cwCompanyId: number;
  companyName: string;
  attention: AttentionItem[];
  money: MoneyPanel;
  projects: ProjectPanelItem[];
  service: ServicePanelItem[];
  delivery: DeliveryPanelItem[];
  rmas: RmaPanelItem[];
  relationship: RelationshipPanel;
}

export interface RmrAgreement {
  id: number;
  name: string;
  type: string;
  billingCycle: string;
  billAmount: number;
  monthlyEquivalent: number;
  startDate: string | null;
  endDate: string | null;
}

export interface WarrantyAgreement {
  id: number;
  name: string;
  coverageAmount: number;
  startDate: string | null;
  endDate: string | null;
  status: string;
  daysUntilExpiry: number | null;
  consumed: number | null;   // null when no matching ProjectWarranty record
  remaining: number | null;  // null when no matching ProjectWarranty record
}

export interface AccountAgreements {
  rmr: { monthlyTotal: number; count: number; agreements: RmrAgreement[] };
  warranty: {
    activeCoverageTotal: number;
    granted: number;
    consumed: number;
    remaining: number;
    agreements: WarrantyAgreement[];
    expiring: { in30: number; in60: number; in90: number; items: WarrantyAgreement[] };
    openRmaCount: number;
  };
}
